import { ColumnApi, GridApi } from "ag-grid-community";
import { fromJS, List, Map, Set } from "immutable";
import { handleActions } from "redux-actions";

import {
    IAssortmentErrorAction,
    ICollectionRequestDeletePendingMutationAction,
    ICollectionSetPendingMutationAction,
    ICollectionSyncErrorThrowAction,
} from "actions/collections";
import { IOperationGroup, MutableType } from "actions/mutate";
import regexes from "components/draft-js-mention/utils/hashtagRegex";
import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import {
    ALLOCATION_DIMENSIONS_NAMESPACE,
    ALLOCATION_DIMENSION_ID_PREFIX,
    DELIVERY_PURCHASE_ORDERS_NAMESPACE,
    ITEMS_NAMESPACE,
    ITEM_DISPLAY_PREFERENCES_NAMESPACE,
    Kinds,
    REVIEW_PLANS_NAMESPACE,
    ROOT_NAMESPACE,
} from "constants/product-collection";
import { IImageCellDimensions } from "constants/types";
import { RollupType } from "containers/folders/types/server-rollups";
import { ISuggestionObject } from "containers/product-collections/product-collection-cart";
import { AllocationDimensionKind, IUpdateAllocationDimensionValue } from "helpers/allocation-dimension";

import { buildServerRollupRowNodeId, ISnapshotItem } from "helpers/server-rollup";
import { IPendingMutation } from "lib/dexie-db-service/schemas";
import { UpdateAllocationDimensionAttributeCompleteAction } from "actions/allocation-dimension";
import { parseSchema, ITotalFormulaFields, SchemaKeyMap } from "helpers/product-collection";
import { makeDependentFieldsMapForLookup } from "epics/collections/helpers/collection";
import { CreateQueueActionPayload, TargetsQueueType } from "actions/collections/collection-target";
import { Option } from "components/product-collection/footer/components/left-side/manage-distribution-modal/components/select";
import { DEFAULT_DISTRIBUTION_OPTION } from "components/product-collection/footer/components/left-side/manage-distribution-modal/manage-distribution-modal";
import { Distribution } from "actions/collections/collection-settings";

interface IHistory {
    down: IOperationGroup;
    key: string;
    mutableType: MutableType;
    objectId: string;
    up: IOperationGroup;
    historyGroupId: string;
}

export interface IDoorTotal {
    classifications?: object;
    units: number;
    retail: number;
    wholesale: number;
    sku?: string;
    skus?: { [key: string]: boolean } | number; // number for rollup poc
    depth?: number;
    deliveries?: object;

    totalProducts?: number; // for rollup poc
    totalRows?: number; // for rollup poc
}

export interface IDoorTotals {
    [door: string]: IDoorTotal;
}

export interface IDoorTotalRow {
    doorMetrics?: { [key: string]: number };
    totalMetrics?: { [key: string]: number };
}

export interface IDoorTotalDelta {
    door: string;
    classifications?: any;
    totalConversionRate: number;
    downTotal: IDoorTotal;
    upTotal: IDoorTotal;
}

interface ISizePacksPayload {
    error?: boolean;
    newSizePacks?: { [key: string]: { [key1: string]: IPackInfo } };
}

interface IExportLogsPayload {
    results: List<Map<string, any>>;
    hasMoreExportLogs: boolean;
    shouldAppend?: boolean;
}
export interface PackUnit {
    id: string;
    normalized_key: string;
    units: number;
}

export enum PackTypes {
    CasePack = "CASEPACK",
    PrePack = "PREPACK",
    Multiples = "MULTIPLE",
}
export interface IPackInfo {
    id: string;
    name: string;
    brand: string;
    brandIdentifier: string;
    packType: PackTypes;
    packUnits: PackUnit[];
    packUPC?: string;
    active: boolean;
}

export interface IAuthorPayload {
    label: string;
    name: string;
    email?: string;
}

export type HistoryEntry = IHistory[];

export type ParentFieldsMap = Map<string, Array<string>>;

export interface ICollectionState {
    // Not the best data type but works with the suggestion plugin which needs an object
    hashtags: Set<string>;
    error?: boolean;
    gridApi?: GridApi;
    columnApi?: ColumnApi;
    scrollToIndex?: number;
    sessionId?: string;
    uploading: boolean;
    syncingProducts: boolean;
    allocatingAssortment: boolean;
    findingSizes: boolean;
    bulkAddingItems: boolean;
    bulkAddProductsMissing: string[];
    activeUserSessions: Map<string, any>;
    focusOnCellMap: Map<string, any>;
    syncError?: any;
    isOutOfDate: boolean;
    collectionExport: Map<string, any>;
    collectionExportLogs: List<Map<string, any>>;
    hasMoreExportLogs: boolean;
    isExporting: boolean;
    collectionPOIs?: List<string>;
    hasMorePOIResults: boolean;
    isPOIFetching: boolean;
    syncingItems: boolean;
    syncingCollection: boolean;
    syncingItemDisplayPreferences: boolean;
    syncingAllocationDimension: boolean;
    syncingDeliveryPurchaseOrders: boolean;
    syncingReviewPlans: boolean;
    undo: HistoryEntry[];
    redo: HistoryEntry[];
    isFetchingProducts: boolean;
    budgetIds: string[];
    variantIds: string[];
    productError: any;
    assortmentError?: string;
    productId: any;
    isFetchingPurchaseOrders: boolean;
    purchaseOrdersError: any;
    purchaseOrders: any;
    purchaseOrder: any;
    isCreatingPurchaseOrder: boolean;
    isFetchingPurchaseOrder: boolean;
    isUpdatingPurchaseOrder: boolean;
    copiedDoorAllocation: any;
    copiedDoorAllocationName: string;
    suggestions: ISuggestionObject;
    doorTotals?: IDoorTotals;
    doorTotalsFiltered?: IDoorTotals;
    doorTotalRowFiltered?: IDoorTotalRow;
    unprocessedDoorUnitsDelta: List<IDoorTotalDelta>;
    pendingMutations: Map<string, IPendingMutation | null>;
    collectionId: string;
    collectionItemId: string;
    calculatingDoorTotals: boolean;
    calculatingFilteredDoorTotals: boolean;
    // This is used to queue immediate changes to allocation dimension values.
    // Use case: start assortment modal where user can exclude certain dates or doors.
    queuedAllocationDimensionValues: {
        [allocationDimensionId: string]: IUpdateAllocationDimensionValue[];
    };
    allocationDimensionIds: string[];
    reviewPlanRows: object[];
    reviewPlan: object;
    sizingAllocationEnabled: boolean;
    merchantId: string;
    collectionItemMerchantId: string;
    driveItem: nuorder.folder.v1.Item;
    driveItemExportCount?: number;
    sizingCurveEnabled: boolean;
    sizingRangeEnabled: boolean;
    sizePackEnabled: boolean;
    assortmentsCollectedSize?: number;
    assortmentsSize?: number;
    assortmentdIds?: string[];
    collectionsRefs?: { [collectionId: string]: string }; // collectionId: name
    rollupSnapshot?: object;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    isLoadingSuggestions: boolean;
    serverRollupSnapshotId?: string;
    createServerRollupSnapshotError?: any;
    recreateServerRollupSnapshot?: boolean;
    isCollectionSettingsOpen?: boolean;
    snapshotItems?: Map<string, ISnapshotItem>;
    fetchSnapshotItemError?: any;
    isFetchingSnapshotItem?: boolean;
    isServerRollup?: boolean;
    validationAuthors: { [key: string]: Omit<IAuthorPayload, "label"> };
    imageCellWidth?: number;
    imageCellHeight?: number;
    collectionDebuggerRevision: string;
    collectionDebuggerSnapshot: any;
    syncingInventoyUnits: boolean;
    totalFormulaFields: ITotalFormulaFields;
    dependentFieldsLookupMap: ParentFieldsMap;
    merchantIdentifierFields: string[];
    targetsQueue: {
        operations: any[];
        type: TargetsQueueType | undefined;
        targetName: string | undefined;
    };
    isDistributionModalOpen: boolean;
    selectedDistribution: Distribution;
}

export const initialState: ICollectionState = {
    collectionId: undefined,
    scrollToIndex: undefined,
    gridApi: undefined,
    columnApi: undefined,
    hashtags: Set<string>(),
    error: undefined,
    sessionId: undefined,
    uploading: false,
    syncingProducts: false,
    allocatingAssortment: false,
    findingSizes: false,
    bulkAddingItems: false,
    bulkAddProductsMissing: [],
    activeUserSessions: Map<string, any>(),
    focusOnCellMap: Map<string, any>(),
    syncError: undefined,
    isOutOfDate: false,
    collectionExport: Map<string, any>(),
    collectionExportLogs: List<Map<string, any>>(),
    calculatingDoorTotals: false,
    calculatingFilteredDoorTotals: false,
    hasMoreExportLogs: true,
    isExporting: false,
    collectionPOIs: undefined,
    hasMorePOIResults: false,
    isPOIFetching: false,
    syncingItems: false,
    syncingCollection: false,
    syncingItemDisplayPreferences: false,
    syncingAllocationDimension: false,
    syncingDeliveryPurchaseOrders: false,
    syncingReviewPlans: false,
    undo: [],
    redo: [],
    isFetchingProducts: false,
    budgetIds: [],
    variantIds: [],
    productError: undefined,
    productId: undefined,
    isFetchingPurchaseOrders: false,
    purchaseOrdersError: undefined,
    purchaseOrders: undefined,
    purchaseOrder: undefined,
    isCreatingPurchaseOrder: false,
    isFetchingPurchaseOrder: false,
    isUpdatingPurchaseOrder: false,
    copiedDoorAllocation: undefined,
    copiedDoorAllocationName: undefined,
    suggestions: {},
    doorTotals: undefined, // if undefined, it means totals are calculating so for UI show loading state
    unprocessedDoorUnitsDelta: List<IDoorTotalDelta>(),
    pendingMutations: Map(),
    assortmentError: null,
    queuedAllocationDimensionValues: {},
    allocationDimensionIds: [],
    sizingAllocationEnabled: false,
    sizingCurveEnabled: false,
    sizingRangeEnabled: false,
    sizePackEnabled: false,
    reviewPlanRows: [],
    reviewPlan: undefined,
    merchantId: undefined,
    collectionItemId: undefined,
    collectionItemMerchantId: undefined,
    driveItem: undefined,
    assortmentsCollectedSize: 0,
    assortmentsSize: 0,
    sizePacks: {},
    isLoadingSuggestions: false,
    snapshotItems: Map(),
    isFetchingSnapshotItem: false,
    validationAuthors: {},
    collectionDebuggerRevision: "1",
    collectionDebuggerSnapshot: undefined,
    syncingInventoyUnits: false,
    isCollectionSettingsOpen: false,
    totalFormulaFields: {},
    dependentFieldsLookupMap: Map(),
    merchantIdentifierFields: [],
    targetsQueue: {
        operations: [],
        type: undefined,
        targetName: undefined,
    },
    isDistributionModalOpen: false,
    selectedDistribution: DEFAULT_DISTRIBUTION_OPTION,
};

const EMPTY_LIST = List<IDoorTotalDelta>();
export const collection = handleActions(
    {
        [COLLECTIONS.COLLECTION.DOOR_TOTALS.CALCULATE_FOR_MATCHING_ROWS]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            const mutatedState = {
                ...state,
                calculatingDoorTotals: true,
                doorTotals: undefined,
                unprocessedDoorUnitsDelta: EMPTY_LIST,
            };

            if (payload.useAsFilteredTotals) {
                mutatedState.calculatingFilteredDoorTotals = true;
                mutatedState.doorTotalsFiltered = undefined;
            }

            return mutatedState;
        },
        [COLLECTIONS.COLLECTION.DOOR_TOTALS.CALCULATION_RETURNED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            const mutatedState = {
                ...state,
                calculatingDoorTotals: false,
                doorTotals: payload.totals,
                unprocessedDoorUnitsDelta: EMPTY_LIST,
            };

            if (payload.useAsFilteredTotals) {
                mutatedState.calculatingFilteredDoorTotals = false;
                mutatedState.doorTotalsFiltered = payload.totals;
            }

            return mutatedState;
        },
        [COLLECTIONS.COLLECTION.DOOR_TOTALS_FILTERED.CALCULATE_FOR_MATCHING_ROWS]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                calculatingFilteredDoorTotals: true,
                doorTotalsFiltered: undefined,
                unprocessedDoorUnitsDelta: EMPTY_LIST,
            };
        },
        [COLLECTIONS.COLLECTION.DOOR_TOTALS_FILTERED.CALCULATION_RETURNED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                calculatingFilteredDoorTotals: false,
                doorTotalsFiltered: payload.totals,
                unprocessedDoorUnitsDelta: EMPTY_LIST,
            };
        },
        [COLLECTIONS.COLLECTION.DOOR_TOTAL_ROW_FILTERED.CALCULATION_RETURNED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                doorTotalRowFiltered: payload.totals,
            };
        },
        [COLLECTIONS.COLLECTION.DOOR_TOTALS.SET_DOOR_TOTAL_DELTA_QUEUE]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                unprocessedDoorUnitsDelta: List(payload.queue),
            };
        },
        [COLLECTIONS.COLLECTION.REFRESH_PAGE]: (state: ICollectionState): ICollectionState => {
            // eslint-disable-next-line no-alert
            window.alert("Sorry, something has gone wrong and we must reload the page.");
            window.location.reload();

            return {
                ...state,
            };
        },
        [COLLECTIONS.COLLECTION.HISTORY.CLEAR]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                redo: [],
                undo: [],
            };
        },
        [COLLECTIONS.COLLECTION.HISTORY.PUSH]: (state: ICollectionState, { payload }: any): ICollectionState => {
            // const { up, down, key, objectId, mutableType, historyGroupId } = payload;
            const newState = {
                ...state,
                redo: state.redo.length ? [] : state.redo,
                undo: state.undo.concat([payload]),
            };
            return newState;
        },
        [COLLECTIONS.COLLECTION.HISTORY.UPDATE]: (
            state: ICollectionState,
            { payload: { undo, redo } }: any
        ): ICollectionState => {
            return {
                ...state,
                redo,
                undo,
            };
        },
        [COLLECTIONS.COLLECTION.CLEAR]: (): ICollectionState => {
            return initialState;
        },
        [COLLECTIONS.COLLECTION.SESSION.CREATE.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            if (error) {
                return state; // ignore for now
            }
            return {
                ...state,
                sessionId: payload.sessionId,
                error: undefined,
            };
        },
        [MUTATE_OPERATIONS_SENT]: (state: ICollectionState, { payload }: any): ICollectionState => {
            if (payload.objectType === ITEMS_NAMESPACE) {
                return {
                    ...state,
                    syncingItems: true,
                };
            }
            if (payload.objectType === ROOT_NAMESPACE) {
                return {
                    ...state,
                    syncingCollection: true,
                };
            }
            if (payload.objectType === ITEM_DISPLAY_PREFERENCES_NAMESPACE) {
                return {
                    ...state,
                    syncingItemDisplayPreferences: true,
                };
            }
            if (payload.objectType === ALLOCATION_DIMENSIONS_NAMESPACE) {
                return {
                    ...state,
                    syncingAllocationDimension: true,
                };
            }
            if (payload.objectType === DELIVERY_PURCHASE_ORDERS_NAMESPACE) {
                return {
                    ...state,
                    syncingDeliveryPurchaseOrders: true,
                };
            }
            if (payload.objectType === REVIEW_PLANS_NAMESPACE) {
                return {
                    ...state,
                    syncingReviewPlans: true,
                };
            }
            return state;
        },
        [COLLECTIONS.COLLECTION.ITEMS.SYNC.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            return {
                ...state,
                syncError: error ? payload : undefined,
                syncingItems: false,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.SYNC.COMPLETED]: (
            state: ICollectionState
        ): ICollectionState => {
            return {
                ...state,
                syncingItemDisplayPreferences: false,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.SYNC.COMPLETED]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingAllocationDimension: false,
            };
        },
        [COLLECTIONS.COLLECTION.DELIVERY_PURCHASE_ORDER.SYNC.COMPLETE]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingDeliveryPurchaseOrders: false,
            };
        },
        [COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            return {
                ...state,
                syncError: error ? payload : undefined,
                syncingReviewPlans: false,
            };
        },
        [COLLECTIONS.COLLECTION.SYNC.COMPLETED]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingCollection: false,
            };
        },
        [COLLECTIONS.COLLECTION.DRIVE_ITEM.FETCH.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            if (error) {
                return state;
            }

            return {
                ...state,
                driveItem: payload,
                rollupSnapshot: payload.rollupSnashot,
                error: undefined,
            };
        },
        [COLLECTIONS.COLLECTION.DRIVE_ITEM.RENAME.COMPLETED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                driveItem: {
                    ...state.driveItem,
                    name: payload,
                },
            };
        },
        [COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.OPEN_CLOSE]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                isCollectionSettingsOpen: payload,
            };
        },
        [COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.COMPLETED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                driveItemExportCount: null,
                driveItem: {
                    ...state.driveItem,
                    name: payload.name,
                    collection_facet: {
                        ...state.driveItem.collection_facet,
                        ...payload.collection_facet,
                    },
                },
            };
        },
        [COLLECTIONS.COLLECTION.REPORTING.UPDATES.TRANSIENT]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                driveItemExportCount: payload.exportCount,
            };
        },
        [COLLECTIONS.COLLECTION.FETCH.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            if (error) {
                return {
                    ...state,
                    error: payload,
                };
            }
            // calculate tags
            let hashtags = Set<string>();
            payload.items.forEach((item) => {
                Object.keys(item.fields).forEach((key) => {
                    const value = item.fields[key];
                    Object.keys(value).forEach((subkey) => {
                        const subvalue = value[subkey] + "";
                        let match;
                        // eslint-disable-next-line no-cond-assign
                        while ((match = regexes.validHashtag.exec(subvalue))) {
                            const hashtag = match[3] as string;
                            hashtags = hashtags.add(hashtag);
                        }
                    });
                });
            });

            const { totalFormulaFields } = parseSchema(
                fromJS(payload.collection.snapshot.root.mutable_map.fields.item_display_property.struct_value.keys)
            );

            let sizingAllocationEnabled = false;
            Object.keys(payload?.collection?.snapshot?.allocation_dimensions ?? {}).forEach((key) => {
                const dimension = payload.collection.snapshot.allocation_dimensions[key];
                if (
                    dimension?.mutable_mixed?.objects?.field_map?.map?.fields?.subtype?.string_value ===
                    AllocationDimensionKind.Size
                ) {
                    sizingAllocationEnabled = true;
                }
            });

            let sizingCurveEnabled = false,
                sizingRangeEnabled = false,
                sizePackTypeEnabled = false,
                sizePackSelectedEnabled = false;
            const itemDisplayProperties =
                payload?.collection?.snapshot?.root?.mutable_map?.fields?.item_display_property?.struct_value?.keys ||
                [];
            for (const itemDisplayProperty of itemDisplayProperties) {
                if (itemDisplayProperty.kind === Kinds.SIZE_PROFILE_SELECTED) {
                    sizingCurveEnabled = true;
                }

                if (itemDisplayProperty.kind === Kinds.SIZE_CURVE_METHOD) {
                    sizingRangeEnabled = true;
                }

                if (itemDisplayProperty.kind === Kinds.PACKS_TYPE && !itemDisplayProperty.hidden) {
                    sizePackTypeEnabled = true;
                }

                if (itemDisplayProperty.kind === Kinds.PACKS_SELECTED && !itemDisplayProperty.hidden) {
                    sizePackSelectedEnabled = true;
                }

                // Short-circuit when all flags are met. No need to go any further.
                if (sizingCurveEnabled && sizingRangeEnabled && sizePackTypeEnabled && sizePackSelectedEnabled) {
                    break;
                }
            }
            const merchantIdentifierFields =
                payload?.collection?.snapshot?.root?.mutable_map?.fields?.item_display_property?.struct_value
                    ?.identifier_fields || [];

            return {
                ...state,
                collectionId: payload.collection.id,
                collectionItemId: payload.collection.item_id,
                merchantId: payload.merchantId,
                collectionItemMerchantId: payload.collection.merchant_v1_id,
                hashtags,
                error: undefined,
                allocationDimensionIds: payload.collection.item_allocation_dimension_order.map(
                    (d) => ALLOCATION_DIMENSION_ID_PREFIX + d
                ),
                sizingAllocationEnabled,
                sizingCurveEnabled,
                sizingRangeEnabled,
                sizePackEnabled: sizePackTypeEnabled && sizePackSelectedEnabled,
                assortmentsSize: payload.assortmentsSize,
                assortmentsCollectedSize: payload.assortmentsCollectedSize,
                assortmentdIds: payload.assortmentdIds,
                isServerRollup: payload.isRollupMode && payload.rollupType === RollupType.Server,
                collectionsRefs: payload.collection.snapshot.root.mutable_map.fields.collections_refs || {},
                merchantIdentifierFields,
                totalFormulaFields,
            };
        },
        [COLLECTIONS.COLLECTION.DRIVE_ITEM.FETCH.START]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                merchantId: payload.userMerchantId,
                collectionDebuggerRevision: payload.revision,
            };
        },
        [COLLECTIONS.COLLECTION.DRIVE_ITEM.CREATE_ROLLUP_SNAPSHOT.INIT]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                recreateServerRollupSnapshot: true,
                createServerRollupSnapshotError: null,
            };
        },
        [COLLECTIONS.COLLECTION.DRIVE_ITEM.CREATE_ROLLUP_SNAPSHOT.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            return {
                ...state,
                serverRollupSnapshotId: error ? null : payload.id,
                createServerRollupSnapshotError: error ? payload : null,
            };
        },
        [COLLECTIONS.COLLECTION.ROLLUP_SNAPSHOT_ITEM.FETCH.START]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            return {
                ...state,
                fetchSnapshotItemError: null,
                isFetchingSnapshotItem: true,
            };
        },
        [COLLECTIONS.COLLECTION.ROLLUP_SNAPSHOT_ITEM.FETCH.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            return {
                ...state,
                ...(!error && {
                    snapshotItems: state.snapshotItems.setIn(
                        [buildServerRollupRowNodeId(payload.itemId, payload.currentProduct?.collection_id)],
                        fromJS(payload.currentProduct)
                    ),
                }),
                fetchSnapshotItemError: error ? payload : null,
                isFetchingSnapshotItem: false,
            };
        },
        [COLLECTIONS.COLLECTION.ROLLUP_SNAPSHOT_ITEM.SET_ITEM]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                snapshotItems: state.snapshotItems.setIn(
                    [buildServerRollupRowNodeId(payload.itemId, payload.collectionId)],
                    fromJS(payload.product)
                ),
            };
        },
        [COLLECTIONS.COLLECTION.HASHTAGS.ADD]: (state: ICollectionState, { payload }: any): ICollectionState => {
            let { hashtags } = state;
            if (!payload.hashtags) {
                return state;
            }
            hashtags = hashtags.merge(payload.hashtags);
            return {
                ...state,
                hashtags,
            };
        },
        [COLLECTIONS.COLLECTION.GRID.REGISTER]: (state: ICollectionState, { payload }: any): ICollectionState => {
            if (!payload.gridApi && !payload.columnApi) {
                return state;
            }

            return {
                ...state,
                gridApi: payload.gridApi,
                columnApi: payload.columnApi,
            };
        },
        [COLLECTIONS.COLLECTION.GRID.UNREGISTER]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                gridApi: undefined,
                columnApi: undefined,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.SCROLL_TO]: (
            state: ICollectionState,
            { payload: { index } }: any
        ): ICollectionState => {
            return {
                ...state,
                scrollToIndex: index,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.SYNC.ERROR]: (state: ICollectionState, { payload }: any): ICollectionState => {
            return {
                ...state,
                syncError: payload,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.ERROR]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                syncError: payload,
            };
        },
        [COLLECTIONS.COLLECTION_MEDIA.CREATE.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                uploading: true,
            };
        },
        [COLLECTIONS.COLLECTION.DEBUGGER_SNAPSHOT.COMPLETED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                collectionDebuggerSnapshot: payload,
            };
        },
        [COLLECTIONS.COLLECTION_MEDIA.CREATE.COMPLETED]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                uploading: false,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CLEAR]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isOutOfDate: false,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isOutOfDate: false,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                isOutOfDate: payload && payload.isOutOfDate ? payload.isOutOfDate : false,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingProducts: true,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingProducts: false,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                allocatingAssortment: true,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATIONS.ALLOCATE.STOP]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                allocatingAssortment: false,
            };
        },
        [COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                findingSizes: true,
            };
        },
        [COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.STOP]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                findingSizes: false,
            };
        },
        [COLLECTIONS.COLLECTION.SIZING.SIZE_RANGES.MATCH.COMPLETED]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                findingSizes: false,
            };
        },
        [COLLECTIONS.COLLECTION.SESSION.CLEAR]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                sessionId: undefined,
            };
        },
        [COLLECTIONS.COLLECTION.SESSION.UPDATES.UNSUBSCRIBE]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                activeUserSessions: Map<string, any>(),
                focusOnCellMap: Map<string, any>(),
            };
        },
        [COLLECTIONS.COLLECTION.SESSION.UPDATES.RECEIVED]: (
            state: ICollectionState,
            { payload: { activeUserSessions, focusOnCellMap } }
        ): ICollectionState => {
            return {
                ...state,
                activeUserSessions,
                focusOnCellMap,
            };
        },
        [COLLECTIONS.COLLECTION.EXPORT.CREATE.START]: (state: ICollectionState) => {
            return {
                ...state,
                isExporting: true,
            };
        },
        [COLLECTIONS.COLLECTION.EXPORT.CREATE.COMPLETED]: (
            state: ICollectionState,
            { error, payload: { collectionExport } }
        ) => {
            if (error) {
                return {
                    ...state,
                    isExporting: false,
                    collectionExport: Map({ error }),
                };
            }
            return {
                ...state,
                isExporting: false,
                collectionExport,
            };
        },
        [COLLECTIONS.COLLECTION.EXPORT.RECEIVED]: (state: ICollectionState, { payload: { collectionExport } }) => {
            return {
                ...state,
                collectionExport,
            };
        },
        [COLLECTIONS.COLLECTION.EXPORT.CLEAR]: (state: ICollectionState) => {
            return {
                ...state,
                collectionExport: initialState.collectionExport,
            };
        },
        [COLLECTIONS.COLLECTION.POIS.FETCH.START]: (state: ICollectionState, { payload: { nextPage } }: any) => {
            return {
                ...state,
                collectionPOIs: nextPage ? state.collectionPOIs : initialState.collectionPOIs,
                isPOIFetching: true,
            };
        },
        [COLLECTIONS.COLLECTION.POIS.FETCH.STOP]: (
            state: ICollectionState,
            { error, payload: { results, hasMoreResults = false } }: any
        ): ICollectionState => {
            if (error) {
                return state;
            }
            const collectionPOIs = state.collectionPOIs || List<string>();
            return {
                ...state,
                collectionPOIs: collectionPOIs.concat(results) as List<string>,
                hasMorePOIResults: hasMoreResults,
                isPOIFetching: false,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.BULK.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                bulkAddingItems: true,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.BULK.COMPLETED]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                bulkAddingItems: false,
                bulkAddProductsMissing: payload,
            };
        },
        [COLLECTIONS.COLLECTION.ITEMS.BULK.MISSING_PRODUCTS.VIEWED]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                bulkAddProductsMissing: [],
            };
        },
        [COLLECTIONS.COLLECTION.PRODUCT.FETCH.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isFetchingProducts: true,
                variantIds: [],
            };
        },
        [COLLECTIONS.COLLECTION.PRODUCT.FETCH.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                    productError: payload,
                    productId: undefined,
                    isFetchingProducts: false,
                };
            }
            return {
                ...state,
                productId: payload,
                isFetchingProducts: false,
            };
        },
        [COLLECTIONS.COLLECTION.SUGGESTIONS.FETCH.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
            };
        },
        [COLLECTIONS.COLLECTION.SUGGESTIONS.FETCH.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                };
            }
            return {
                ...state,
                suggestions: payload,
            };
        },
        [COLLECTIONS.COLLECTION.PRODUCT.VARIANTS.FETCH.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            if (error) {
                return state;
            }
            return {
                ...state,
                variantIds: payload as string[],
            };
        },
        [COLLECTIONS.COLLECTION.BUDGET.CREATE.COMPLETED]: (
            state: ICollectionState,
            { error, payload: { budget } }: any
        ): ICollectionState => (error ? state : { ...state, budgetIds: state.budgetIds.concat(budget.id) }),
        [COLLECTIONS.COLLECTION.BUDGET.FETCH.COMPLETED]: (
            state: ICollectionState,
            { error, payload: { budgets } }: any
        ): ICollectionState => (error ? state : { ...state, budgetIds: budgets.map(({ id }) => id) }),
        [COLLECTIONS.COLLECTION.BUDGET.DELETE.COMPLETED]: (
            state: ICollectionState,
            { error, payload: { budgetId } }: any
        ): ICollectionState =>
            error ? state : { ...state, budgetIds: state.budgetIds.filter((id) => id !== budgetId) },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDERS.FETCH.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isFetchingPurchaseOrders: true,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDERS.FETCH.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                    purchaseOrdersError: payload,
                    isFetchingPurchaseOrders: false,
                };
            }
            return {
                ...state,
                purchaseOrders: payload,
                isFetchingPurchaseOrders: false,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDER.CREATE.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isCreatingPurchaseOrder: true,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDER.CREATE.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                    purchaseOrdersError: payload,
                    isCreatingPurchaseOrder: false,
                };
            }
            const purchaseOrder = {
                [(payload as any).vendor_id]: payload,
            };
            return {
                ...state,
                purchaseOrder: payload,
                purchaseOrders: {
                    ...(state.purchaseOrders || {}),
                    ...purchaseOrder,
                },
                isCreatingPurchaseOrder: false,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDER.FETCH.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isFetchingPurchaseOrder: true,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDER.FETCH.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                    purchaseOrdersError: payload,
                    isFetchingPurchaseOrder: false,
                };
            }
            return {
                ...state,
                purchaseOrder: payload,
                isFetchingPurchaseOrder: false,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDER.UPDATE.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isUpdatingPurchaseOrder: true,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDER.UPDATE.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                    purchaseOrdersError: payload,
                    isUpdatingPurchaseOrder: false,
                };
            }
            const purchaseOrder = {
                [(payload as any).vendor_id]: payload,
            };
            return {
                ...state,
                purchaseOrder: payload,
                purchaseOrders: {
                    ...(state.purchaseOrders || {}),
                    ...purchaseOrder,
                },
                isUpdatingPurchaseOrder: false,
            };
        },
        [COLLECTIONS.COLLECTION.PURCHASE_ORDERS.CREATE.COMPLETED]: (state: ICollectionState, { error, payload }) => {
            if (error) {
                return {
                    ...state,
                    purchaseOrdersError: payload,
                    isCreatingPurchaseOrder: false,
                };
            }

            return {
                ...state,
                purchaseOrders: payload,
                isCreatingPurchaseOrder: false,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATIONS.DOOR.COPY_COMPLETE]: (
            state: ICollectionState,
            { payload: { door, doorAllocation } }: any
        ): ICollectionState => {
            return {
                ...state,
                copiedDoorAllocation: doorAllocation,
                copiedDoorAllocationName: door,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATIONS.DOOR.CLEAR]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                copiedDoorAllocation: undefined,
                copiedDoorAllocationName: undefined,
            };
        },
        [COLLECTIONS.COLLECTION.PENDING_MUTATION.SET]: (
            state: ICollectionState,
            { payload }: ICollectionSetPendingMutationAction
        ): ICollectionState => {
            return {
                ...state,
                pendingMutations: state.pendingMutations.setIn([payload.objectId], payload.pendingMutation),
            };
        },
        [COLLECTIONS.COLLECTION.PENDING_MUTATION.REQUEST_DELETE]: (
            state: ICollectionState,
            { payload }: ICollectionRequestDeletePendingMutationAction
        ): ICollectionState => {
            return {
                ...state,
                pendingMutations: state.pendingMutations.remove(payload.objectId),
            };
        },
        [COLLECTIONS.COLLECTION.ERROR.SYNC_ERROR_CLEAR]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncError: undefined,
            };
        },
        [COLLECTIONS.COLLECTION.ERROR.SYNC_ERROR_THROW]: (
            state: ICollectionState,
            { payload }: ICollectionSyncErrorThrowAction
        ): ICollectionState => {
            return {
                ...state,
                syncError: payload,
            };
        },
        [COLLECTIONS.COLLECTION.ERROR.ALLOCATION_LOOK_UP_SET_ERROR]: (
            state: ICollectionState,
            { payload }: IAssortmentErrorAction
        ): ICollectionState => {
            return {
                ...state,
                assortmentError: payload.errorMessage,
            };
        },
        [COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.LOAD]: (state: ICollectionState, { payload }) => {
            return {
                ...state,
                reviewPlanRows: payload.reviewPlanRows,
            };
        },
        [COLLECTIONS.COLLECTION.REVIEW_PLANS.LOAD]: (state: ICollectionState, { payload }) => {
            return {
                ...state,
                reviewPlan: payload.reviewPlan,
            };
        },
        [COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.UPDATE]: (state: ICollectionState, { payload }) => {
            const { reviewPlanRows } = state;
            const { reviewPlan, indexes } = payload as any;
            indexes.forEach((index) => {
                reviewPlanRows[index] = {
                    ...(reviewPlanRows[index] || {}),
                    plan: reviewPlan,
                };
            });
            return {
                ...state,
                reviewPlanRows: [...reviewPlanRows],
            };
        },
        [COLLECTIONS.COLLECTION.SIZE_PACKS.FETCH.START]: (state: ICollectionState) => {
            return { ...state, isLoadingSuggestions: true };
        },

        [COLLECTIONS.COLLECTION.SIZE_PACKS.FETCH.COMPLETED]: (
            state: ICollectionState,
            { payload }: { payload: ISizePacksPayload }
        ) => {
            if (!payload || payload.error || !payload.newSizePacks) {
                return state;
            }

            const { sizePacks } = state;
            return { ...state, isLoadingSuggestions: false, sizePacks: { ...sizePacks, ...payload.newSizePacks } };
        },
        [COLLECTIONS.COLLECTION.EXPORT.LOGS.FETCH.COMPLETED]: (
            state: ICollectionState,
            { payload }: { payload: IExportLogsPayload }
        ) => {
            const { results, hasMoreExportLogs, shouldAppend } = payload;
            const collectionExportLogs = shouldAppend
                ? state.collectionExportLogs.concat(results).toList()
                : results.toList();
            return { ...state, collectionExportLogs, hasMoreExportLogs };
        },
        [COLLECTIONS.COLLECTION.EXPORT.LOGS.CLEAR]: (state: ICollectionState) => {
            return { ...state, collectionExportLogs: List<Map<string, any>>(), hasMoreExportLogs: true };
        },
        [COLLECTIONS.COLLECTION.VALIDATION_LOG.AUTHORS.FETCH.COMPLETED]: (
            state: ICollectionState,
            { payload }: { payload: IAuthorPayload }
        ) => {
            const { label, name, email } = payload;
            return { ...state, validationAuthors: { ...state.validationAuthors, [label]: { name, email } } };
        },
        [COLLECTIONS.COLLECTION.GRID.SET_IMAGE_CELL_DIMENSIONS]: (
            state: ICollectionState,
            { payload }: { payload: IImageCellDimensions }
        ) => {
            return {
                ...state,
                imageCellWidth: payload.width,
                imageCellHeight: payload.height,
            };
        },
        [COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.START]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingInventoyUnits: true,
            };
        },
        [COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.STOP]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                syncingInventoyUnits: false,
            };
        },
        [COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.ATTRIBUTES.UPDATE.COMPLETE]: (
            state: ICollectionState,
            _action: UpdateAllocationDimensionAttributeCompleteAction
        ): ICollectionState => {
            return {
                ...state,
            };
        },
        [COLLECTIONS.COLLECTION.ITEM_DISPLAY_PROPERTY.INIT_DEPENDENTS_FIELDS_LOOKUP_MAP]: (
            state: ICollectionState,
            { payload }: { payload: { schemaList: List<SchemaKeyMap> } }
        ): ICollectionState => {
            return {
                ...state,
                dependentFieldsLookupMap: makeDependentFieldsMapForLookup(payload.schemaList),
            };
        },
        [COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.COMPLETED]: (
            state: ICollectionState,
            { error, payload }: any
        ): ICollectionState => {
            return {
                ...state,
                syncError: error ? payload : undefined,
                syncingItems: false,
            };
        },
        [COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.ERROR]: (
            state: ICollectionState,
            { payload }: any
        ): ICollectionState => {
            return {
                ...state,
                syncError: payload,
            };
        },
        [COLLECTIONS.COLLECTION.TARGET_METRICS.QUEUE.CREATE]: (
            state: ICollectionState,
            { payload }: { payload: CreateQueueActionPayload }
        ): ICollectionState => {
            return {
                ...state,
                targetsQueue: {
                    ...state.targetsQueue,
                    targetName: payload.targetName,
                    operations: payload.operations,
                },
            };
        },
        [COLLECTIONS.COLLECTION.TARGET_METRICS.QUEUE.SHIFT]: (state: ICollectionState): ICollectionState => {
            const operationsQueueCopy = [...state.targetsQueue.operations];

            //removing the first element
            operationsQueueCopy.shift();

            return {
                ...state,
                targetsQueue: {
                    ...state.targetsQueue,
                    operations: operationsQueueCopy,
                },
            };
        },
        [COLLECTIONS.COLLECTION.TARGET_METRICS.QUEUE.UPDATE_TYPE]: (
            state: ICollectionState,
            { payload }: { payload: TargetsQueueType }
        ): ICollectionState => {
            return {
                ...state,
                targetsQueue: {
                    ...state.targetsQueue,
                    type: payload,
                },
            };
        },
        [COLLECTIONS.COLLECTION.TARGET_METRICS.QUEUE.CLEAR]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                targetsQueue: initialState.targetsQueue,
            };
        },
        [COLLECTIONS.COLLECTION.DISTRIBUTION.MODAL.OPEN]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isDistributionModalOpen: true,
            };
        },
        [COLLECTIONS.COLLECTION.DISTRIBUTION.MODAL.CLOSE]: (state: ICollectionState): ICollectionState => {
            return {
                ...state,
                isDistributionModalOpen: false,
            };
        },
        [COLLECTIONS.COLLECTION.DISTRIBUTION.SELECT]: (
            state: ICollectionState,
            { payload }: { payload: Option | undefined }
        ): ICollectionState => {
            return {
                ...state,
                selectedDistribution: payload,
            };
        },
    },
    initialState
);
